export type ParsedField = { label: string; value: string };

const ACTION_CODES = new Set(["A", "N", "D", "L"]);
const ARTICLE_TYPES = new Set(["A", "ART", "ARTIKEL", "1"]);
const PRICE_TYPES = new Set(["P", "PREIS", "DATPREIS", "2"]);
const DISCOUNT_TYPES = new Set(["R", "RAB", "RABATT", "3"]);
const UOM_VALUES = new Set([
  "PCE", "STK", "M", "KG", "L", "M2", "M3", "PAK", "SET",
  "ROL", "BDL", "PAR", "KAR", "BLT", "PR", "LFM",
]);

export function parseDateanormRecord(raw: string): ParsedField[] | null {
  if (!raw || !raw.includes(";")) return null;
  const fields = raw.split(";");
  if (fields.length < 3) return null;

  const recordType = f(fields, 0).toUpperCase();
  const hasAction = ACTION_CODES.has(f(fields, 1).toUpperCase());

  if (ARTICLE_TYPES.has(recordType)) {
    return parseArticle(fields, hasAction);
  }
  if (PRICE_TYPES.has(recordType)) {
    return parsePrice(fields, hasAction);
  }
  if (DISCOUNT_TYPES.has(recordType)) {
    return parseDiscount(fields);
  }
  return null;
}

function f(fields: string[], i: number): string {
  return (fields[i] ?? "").trim();
}

function parseArticle(fields: string[], hasAction: boolean): ParsedField[] {
  if (hasAction) {
    const isDtn5 = fields.length > 14 || UOM_VALUES.has(f(fields, 5).toUpperCase());
    if (isDtn5) {
      return build([
        ["Satzart", recordTypeLabel(f(fields, 0))],
        ["Aktion", actionLabel(f(fields, 1))],
        ["Artikelnummer", f(fields, 2)],
        ["Kurztext 1", f(fields, 3)],
        ["Kurztext 2", f(fields, 4)],
        ["Mengeneinheit", f(fields, 5)],
        ["Preiskennzeichen", f(fields, 6)],
        ["Preiseinheit", f(fields, 7)],
        ["Listenpreis", fmtPrice(fields[8])],
        ["Rabattgruppe", f(fields, 9)],
        ["Warengruppe", f(fields, 10)],
        ["Hersteller-Artikelnr.", f(fields, 17) || f(fields, 16)],
        ["Hersteller", f(fields, 15)],
        ["EAN", f(fields, 18)],
      ]);
    }
    return build([
      ["Satzart", recordTypeLabel(f(fields, 0))],
      ["Aktion", actionLabel(f(fields, 1))],
      ["Artikelnummer", f(fields, 2)],
      ["Kurztext 1", f(fields, 4)],
      ["Kurztext 2", f(fields, 5)],
      ["Mengeneinheit", f(fields, 8)],
      ["Listenpreis", fmtPrice(fields[9])],
      ["Rabattgruppe", f(fields, 10)],
      ["Warengruppe", f(fields, 11)],
      ["Hersteller-Artikelnr.", f(fields, 12)],
    ]);
  }

  return build([
    ["Satzart", recordTypeLabel(f(fields, 0))],
    ["Artikelnummer", f(fields, 1)],
    ["Bezeichnung", f(fields, 2)],
    ["Mengeneinheit", f(fields, 3)],
    ["Listenpreis", fmtPrice(fields[4])],
    ["Rabattgruppe", f(fields, 5)],
    ["Warengruppe", f(fields, 6)],
    ["EAN", f(fields, 7)],
    ["Hersteller-Artikelnr.", f(fields, 8)],
    ["Währung", f(fields, 9)],
    ["Gültig ab", f(fields, 10)],
  ]);
}

function parsePrice(fields: string[], hasAction: boolean): ParsedField[] {
  if (hasAction) {
    const result: ParsedField[] = [
      { label: "Satzart", value: recordTypeLabel(f(fields, 0)) },
      { label: "Aktion", value: actionLabel(f(fields, 1)) },
    ];
    for (let offset = 2; offset < fields.length; offset += 9) {
      const code = f(fields, offset);
      if (!code) break;
      result.push(
        { label: "Artikelnummer", value: code },
        { label: "Preisart", value: priceKindLabel(f(fields, offset + 1)) },
        { label: "Preis", value: fmtPrice(fields[offset + 2]) },
      );
    }
    return result;
  }
  return build([
    ["Satzart", recordTypeLabel(f(fields, 0))],
    ["Artikelnummer", f(fields, 1)],
    ["Preisart", priceKindLabel(f(fields, 2))],
    ["Gültig ab", f(fields, 3)],
    ["Listenpreis", fmtPrice(fields[4])],
    ["Nettopreis", fmtPrice(fields[5])],
    ["Währung", f(fields, 6)],
  ]);
}

function parseDiscount(fields: string[]): ParsedField[] {
  if (!f(fields, 1) && f(fields, 2)) {
    return build([
      ["Satzart", recordTypeLabel(f(fields, 0))],
      ["Rabattgruppe", f(fields, 2)],
      ["Rabatt %", fmtPct(fields[4])],
      ["Bezeichnung", f(fields, 5)],
    ]);
  }
  return build([
    ["Satzart", recordTypeLabel(f(fields, 0))],
    ["Rabattgruppe", f(fields, 1)],
    ["Rabatt %", fmtPct(fields[2])],
    ["Bezeichnung", f(fields, 3)],
  ]);
}

function build(pairs: [string, string][]): ParsedField[] {
  return pairs.filter(([, v]) => v !== "").map(([label, value]) => ({ label, value }));
}

function fmtPrice(raw: string | undefined): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "";
  const num = parseInt(trimmed, 10);
  if (isNaN(num)) return trimmed;
  return `€ ${(num / 100).toFixed(2).replace(".", ",")}`;
}

function fmtPct(raw: string | undefined): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "";
  const num = parseInt(trimmed, 10);
  if (isNaN(num)) return `${trimmed} %`;
  return `${(num / 100).toFixed(2).replace(".", ",")} %`;
}

function recordTypeLabel(code: string): string {
  const upper = code.toUpperCase();
  if (ARTICLE_TYPES.has(upper)) return `Artikelsatz (${code})`;
  if (PRICE_TYPES.has(upper)) return `Preissatz (${code})`;
  if (DISCOUNT_TYPES.has(upper)) return `Rabattsatz (${code})`;
  return code;
}

function actionLabel(code: string): string {
  switch (code.toUpperCase()) {
    case "N": return "Neuanlage";
    case "A": return "Änderung";
    case "D": return "Löschung";
    case "L": return "Löschung";
    default: return code;
  }
}

function priceKindLabel(kind: string): string {
  if (kind === "1") return "Listenpreis (1)";
  if (kind === "2") return "Einkaufspreis (2)";
  if (kind === "3") return "Nettopreis (3)";
  return kind || "";
}
